import {useState, useEffect} from "react"
import cx from "classnames"
import {useTranslation} from "react-i18next"
import momentTz from "moment-timezone"
import {Row, Col, Checkbox, Switch} from "antd"
import moment from "moment"
import {Icon} from "components/Icon"
import {BaseEditor, BaseInput} from "components/inputs"
import {BaseButton, SecondaryButton} from "components"
import {BaseDatePicker, BaseTimePicker} from "components/DateTimePicker"
import {handleError, isInvalidValue, toastError} from "helpers"
import {CalendarEventType, CreateEventPayload} from "types/calendar"
import {DisplayMode} from "types/common"
import {Auth} from "types/auth"
import CampusSelect from "components/CampusSelect"
import styles from "./CalendarEventForm.module.css"
import {useModel} from "hooks"
import {isEmpty, map, size} from "lodash"
import ProgramSelect from "components/ProgramSelect"
import ShiftSelect from "components/ShiftSelect"

type SchoolClosureFormProps = {
    calendarId: number
    isShow?: boolean
    onCreateSuccess: Function
    onClose: () => void
    displayMode?: DisplayMode
}

const SCHOOL_CLOSURE_COLOR = "#ff0000"

export const SchoolClosureForm = (props: SchoolClosureFormProps) => {
    const {isShow, onClose, calendarId, onCreateSuccess, displayMode = "normal"} = props
    const model = useModel()
    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [eventData, setEventData] = useState<any>({
        name: "",
        startDate: "",
        startTime: "",
        endTime: "",
        campus: [],
        programs: [],
        shifts: [],
        allDay: false,
        start_at_timezone: momentTz.tz.guess()
    })
    const {t} = useTranslation(["calendar", "common"])
    const isFullDisplay = displayMode === "normal"

    useEffect(() => {
        if (!isShow) return
        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose()
        }
        document.addEventListener("keydown", handleKeyDown)
        return () => {
            document.removeEventListener("keydown", handleKeyDown)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShow])

    useEffect(() => {
        if (isShow) {
            setEventData({
                name: "",
                startDate: "",
                startTime: "",
                endTime: "",
                campus: [],
                allDay: false,
                start_at_timezone: momentTz.tz.guess()
            })
            setErrors({})
        }
    }, [isShow])

    const onChangeEventData = (key, value) => {
        const newErrors = {...errors}
        const newEventData = {...eventData}

        if (!isInvalidValue(value)) {
            delete newErrors[key]
        }

        newEventData[key] = value

        if (key === "startTime" && value) {
            const endTime = moment(value).add(1, "hour")
            const isSameDate = endTime.clone().startOf("date").isSame(moment(value).startOf("date"))
            newEventData.endTime = isSameDate ? endTime.clone() : moment(value).endOf("date")
        }

        setErrors(newErrors)
        setEventData(newEventData)
    }

    const validate = () => {
        const errorsAux: any = {}
        const mandatoryFields = ["name", "startDate", "campus"]

        if (!eventData.allDay) {
            mandatoryFields.push("startTime", "endTime")
        }

        mandatoryFields.forEach((field) => {
            if (field === "campus") {
                if (isEmpty(eventData.campus)) {
                    errorsAux[field] = true
                }
            } else if (isInvalidValue(eventData[field])) {
                errorsAux[field] = true
            }
        })

        const hasRequiredFieldError = Object.keys(errorsAux).length > 0
        if (hasRequiredFieldError) {
            setErrors(errorsAux)
            toastError(t("common:validation.fieldRequired"))
            return true
        }

        if (!eventData.allDay && moment(eventData.startTime).isSameOrAfter(moment(eventData.endTime))) {
            errorsAux.startTime = true
            errorsAux.endTime = true
            toastError(t("message.endTimeGreaterStartTime"))
            return true
        }

        return false
    }

    const setDateOfTimeValue = (timeValue) => {
        const startDate = moment(eventData.startDate)
        const date = startDate.get("date")
        const month = startDate.get("month")
        const year = startDate.get("year")
        return moment(timeValue).set("date", date).set("month", month).set("year", year)
    }

    const handleOnSubmit = async () => {
        if (isSubmitting) {
            return
        }

        const newEventData = {...eventData}
        if (!eventData.allDay) {
            newEventData.startTime = eventData.startTime ? setDateOfTimeValue(eventData.startTime) : null
            newEventData.endTime = eventData.endTime ? setDateOfTimeValue(eventData.endTime) : null
        }
        setEventData(newEventData)

        const hasError = validate()
        if (hasError) {
            return
        }

        await createEvent()
    }

    const getDataSubmit = () => {
        const {name, startDate, startTime, endTime, campus, programs, shifts, allDay} = eventData
        const ownerUserId = model.viewOtherUserCalendar?.id ? model.viewOtherUserCalendar?.id : +model.user.id

        let duration: number
        let start_at_wall: string

        if (allDay) {
            duration = 3600 * 24
            start_at_wall = moment(startDate).startOf("day").format("YYYY-MM-DD HH:mm:ss")
        } else {
            const startTimeUpdated = `${moment(startDate).format("YYYY-MM-DD")} ${moment(startTime).format("HH:mm:ss")}`
            const endTimeUpdated = `${moment(startDate).format("YYYY-MM-DD")} ${moment(endTime).format("HH:mm:ss")}`
            duration = moment(endTimeUpdated).diff(moment(startTimeUpdated), "seconds")
            start_at_wall = `${moment(startDate).format("YYYY-MM-DD")} ${moment(startTime).format("HH:mm:ss")}`
        }

        const campuses = Array.isArray(campus) ? campus.map((c) => ({id: c.id, name: c.name, code: c.code})) : []

        const payload: CreateEventPayload = {
            calendar_id: calendarId,
            type: CalendarEventType.SchoolClosure,
            name,
            description: eventData.description || "",
            duration,
            owner_user_id: ownerUserId,
            rrule: "",
            start_at_timezone: eventData.start_at_timezone || momentTz.tz.guess(),
            start_at_wall,
            json_data: {
                color: SCHOOL_CLOSURE_COLOR,
                allDay,
                campuses: size(campuses) > 0 ? campuses : undefined,
                programs: size(programs) > 0 ? programs : undefined,
                shifts: size(shifts) > 0 ? shifts : undefined
            }
        }

        return payload
    }

    const createEvent = async () => {
        try {
            setIsSubmitting(true)
            const payload = getDataSubmit()
            await model.addEvent(payload)
            onClose()
            if (onCreateSuccess) {
                onCreateSuccess()
            }
        } catch (error) {
            handleError(error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const renderAllDayField = () => {
        if (isFullDisplay) {
            return (
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    <Col span={2} className={styles.eventPopupLabel} />
                    <Col span={6} className={styles.eventPopupLabel}>
                        <label>{t("calendar.wholeDay")}</label>
                    </Col>
                    <Col span={16}>
                        <Switch
                            size="small"
                            onClick={(v) => onChangeEventData("allDay", v)}
                            checked={eventData.allDay}
                        />
                    </Col>
                </Row>
            )
        } else {
            return (
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    <div className={styles.checkboxWrap}>
                        <div
                            className={cx(styles.checkbox, {[styles.checked]: eventData.allDay})}
                            onClick={() => onChangeEventData("allDay", !eventData.allDay)}>
                            <Checkbox checked={eventData.allDay} />
                            <span className={styles.checkboxTitle}>{t("calendar.wholeDay")}</span>
                        </div>
                    </div>
                </Row>
            )
        }
    }

    const renderAction = () => {
        if (isFullDisplay) {
            return (
                <div className={styles.actionBtn}>
                    <SecondaryButton
                        title={t("common:action.cancel")}
                        onClick={onClose}
                        disabled={isSubmitting}
                        className={styles.cancelBtn}
                    />
                    <BaseButton title={t("common:action.save")} onClick={handleOnSubmit} loading={isSubmitting} />
                </div>
            )
        }
        return (
            <div className={styles.actionBtnPanel}>
                <BaseButton
                    title="Create School Closure"
                    onClick={handleOnSubmit}
                    loading={isSubmitting}
                    className={styles.saveBtn}
                />
            </div>
        )
    }

    return (
        <div className={styles.eventPopupWrap}>
            {isFullDisplay && (
                <div className={styles.eventPopupHeader}>
                    <h1 className={styles.eventPopupTitle}>School Closure</h1>
                </div>
            )}
            <div className={cx(styles.body, {[styles.bodyPanel]: !isFullDisplay})}>
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    {isFullDisplay && (
                        <Col span={2} className={styles.eventPopupLabel}>
                            <Icon icon="PENCIL" className={styles.eventPopupIcon} color="#62B1FF" />
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                        <label>Title</label>
                    </Col>
                    <Col span={isFullDisplay ? 16 : 24}>
                        <BaseInput
                            onChange={(newValue) => onChangeEventData("name", newValue)}
                            placeholder="Enter title"
                            value={eventData.name}
                            className={cx(!!errors["name"] ? styles.error : "")}
                        />
                    </Col>
                </Row>
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    {isFullDisplay && (
                        <Col span={2} className={styles.eventPopupLabel}>
                            <Icon icon="CALENDAR_FILL" className={styles.eventPopupIcon} color="#62B1FF" />
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                        <label>Date</label>
                    </Col>
                    <Col span={isFullDisplay ? 16 : 24}>
                        <BaseDatePicker
                            onChange={(newValue) => onChangeEventData("startDate", newValue)}
                            value={eventData.startDate ? moment(eventData.startDate) : ""}
                            className={cx(styles.shadow, !!errors["startDate"] ? styles.error : "")}
                        />
                    </Col>
                </Row>
                {renderAllDayField()}
                {!eventData.allDay && (
                    <>
                        <Row gutter={[16, 6]} className={styles.rowItem}>
                            {isFullDisplay && (
                                <Col span={2} className={styles.eventPopupLabel}>
                                    <Icon icon="TIME_CLOCK_OUTLINE" className={styles.eventPopupIcon} color="#62B1FF" />
                                </Col>
                            )}
                            <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                                <label>{t("calendar.time")}</label>
                            </Col>
                            <Col span={isFullDisplay ? 8 : 12}>
                                <BaseTimePicker
                                    value={eventData.startTime}
                                    placeholder="Start Time"
                                    format={model.user.timeFormat === Auth.TimeFormat.Time_12 ? "hh:mm A" : "HH:mm"}
                                    onChange={(newValue) => onChangeEventData("startTime", newValue)}
                                    error={errors["startTime"]}
                                />
                            </Col>
                            <Col span={isFullDisplay ? 8 : 12}>
                                <BaseTimePicker
                                    value={eventData.endTime}
                                    placeholder="End Time"
                                    format={model.user.timeFormat === Auth.TimeFormat.Time_12 ? "hh:mm A" : "HH:mm"}
                                    onChange={(newValue) => onChangeEventData("endTime", newValue)}
                                    error={errors["endTime"]}
                                    disabledHours={() => {
                                        if (!eventData.startTime) return []
                                        const startHour = moment(eventData.startTime).hour()
                                        const hours = []
                                        for (let i = 0; i < startHour; i++) {
                                            hours.push(i)
                                        }
                                        return hours
                                    }}
                                    disabledMinutes={() => {
                                        if (!eventData.startTime || !eventData.endTime) return []
                                        const startMoment = moment(eventData.startTime)
                                        const endMoment = moment(eventData.endTime)
                                        if (startMoment.hour() === endMoment.hour()) {
                                            const startMinute = startMoment.minute()
                                            const minutes = []
                                            for (let i = 0; i <= startMinute; i++) {
                                                minutes.push(i)
                                            }
                                            return minutes
                                        }
                                        return []
                                    }}
                                />
                            </Col>
                        </Row>
                    </>
                )}
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    {isFullDisplay && (
                        <Col span={2} className={styles.eventPopupLabel}>
                            <Icon icon="NOTES" className={styles.eventPopupIcon} color="#62B1FF" />
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                        <label>{t("calendar.notes")}</label>
                    </Col>
                    <Col span={isFullDisplay ? 16 : 24}>
                        <BaseEditor
                            onChange={(newValue) => onChangeEventData("description", newValue)}
                            placeholder={t("calendar.notes")}
                            value={eventData.description}
                        />
                    </Col>
                </Row>
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    {isFullDisplay && (
                        <Col span={2} className={styles.eventPopupLabel}>
                            <Icon icon="DOOR_OPEN" className={styles.eventPopupIcon} color="#62B1FF" />
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                        <label>Campuses</label>
                    </Col>
                    <Col span={isFullDisplay ? 16 : 24}>
                        <CampusSelect
                            onChange={(newValue) => onChangeEventData("campus", newValue || [])}
                            placeholder="Select Campuses"
                            value={eventData.campus}
                            error={!!errors["campus"]}
                            isMulti
                            isClearable={false}
                        />
                    </Col>
                </Row>
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    {isFullDisplay && (
                        <Col span={2} className={styles.eventPopupLabel}>
                            <Icon icon="PROGRAM" className={styles.eventPopupIcon} color="#62B1FF" />
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                        <label>Programs</label>
                    </Col>
                    <Col span={isFullDisplay ? 16 : 24}>
                        <ProgramSelect
                            onChange={(newValue) => onChangeEventData("programs", newValue || [])}
                            placeholder="Select Programs"
                            isMulti
                            campusId={map(eventData.campus, (c) => c.id)}
                        />
                    </Col>
                </Row>
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    {isFullDisplay && (
                        <Col span={2} className={styles.eventPopupLabel}>
                            <Icon icon="ACADEMIC" className={styles.eventPopupIcon} color="#62B1FF" />
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                        <label>Shifts</label>
                    </Col>
                    <Col span={isFullDisplay ? 16 : 24}>
                        <ShiftSelect
                            onChange={(newValue) => onChangeEventData("shifts", newValue || [])}
                            placeholder="Select Shifts"
                            isMulti
                        />
                    </Col>
                </Row>
            </div>
            {renderAction()}
        </div>
    )
}
